import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncSelect, type AsyncOption } from '@/components/async-select';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { EMPTY_AUDIT_FILTERS, useAuditStore } from '@/stores/audit.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { dateTime } from '@/lib/format';
import { AUDIT_SUBJECT_KINDS, actionKey, humanizeCode, kindKey, roleKey, subjectHref } from '@/lib/audit';
import { dayLabel } from '@/lib/date-range';
import { labelOr } from '@/lib/labels';
import { staffOptions } from '@/lib/options';
import { ClockIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, TableScroll, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, EmptyState, Input, Pill, Select, TableSkeleton } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';

/**
 * The audit trail (ADR-011).
 *
 * Every admin, money, and PII mutation has been written to `audit_logs` since
 * the first one shipped; until now nothing could read them back, which made it
 * evidence nobody could produce. Read-only, because a trail an administrator
 * can edit is not a trail.
 *
 * Actions and records read as words (`lib/audit.ts`); the code stays in the
 * expanded row for whoever needs to search the logs with it. A person, a kind
 * of record, or one record is a filter a click away, and a record with a page
 * of its own opens it.
 *
 * A row expands to show **what actually changed** rather than dumping both JSON
 * blobs: the fields whose values differ, before beside after. An unchanged
 * field in a twelve-field payload is noise, and noise is what stops anyone
 * reading an audit log a second time.
 */
export function AuditPage() {
  const {
    items,
    loading,
    loadingMore,
    error,
    nextCursor,
    filters,
    total,
    actions,
    load,
    loadMore,
    loadActions,
  } = useAuditStore();

  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [expanded, setExpanded] = useState<string | null>(null);

  // The person filtered by, as the picker or the row named them. The rows
  // themselves name them too, once loaded — so a filter that outlived this
  // page's state still reads as a name.
  const [actorLabel, setActorLabel] = useState('');
  const actorName =
    actorLabel || items.find((entry) => entry.actorUserId === filters.actorUserId)?.actorName || t('actor');

  // An id is pasted or typed whole; waiting for the typing to stop saves a
  // request per keystroke.
  const [subjectDraft, setSubjectDraft] = useState(filters.subjectId);
  useEffect(() => setSubjectDraft(filters.subjectId), [filters.subjectId]);
  useEffect(() => {
    const next = subjectDraft.trim();
    if (next === filters.subjectId) return undefined;
    const timer = setTimeout(() => void load({ subjectId: next }), 400);
    return () => clearTimeout(timer);
  }, [subjectDraft, filters.subjectId, load]);

  useEffect(() => {
    void load();
    void loadActions();
  }, [load, loadActions]);

  const actionLabel = (action: string) => labelOr(t, actionKey(action), humanizeCode(action));
  const kindLabel = (kind: string) => labelOr(t, kindKey(kind), humanizeCode(kind));
  const roleLabel = (role: string) => {
    const key = roleKey(role);
    return key ? t(key as MessageKey) : role;
  };

  const kinds = AUDIT_SUBJECT_KINDS.map((kind) => ({ kind, label: kindLabel(kind) })).sort((a, b) =>
    a.label.localeCompare(b.label, locale),
  );

  const filterByActor = (id: string, name: string) => {
    setActorLabel(name);
    void load({ actorUserId: id });
  };

  const clearAll = () => {
    setActorLabel('');
    void load(EMPTY_AUDIT_FILTERS);
  };

  const chips = [
    ...filters.actions.map((action) => ({
      key: `a-${action}`,
      label: actionLabel(action),
      onRemove: () => void load({ actions: filters.actions.filter((entry) => entry !== action) }),
    })),
    ...(filters.actorUserId
      ? [{ key: 'actor', label: `${t('actor')}: ${actorName}`, onRemove: () => filterByActor('', '') }]
      : []),
    ...(filters.subjectKind
      ? [
          {
            key: 'kind',
            label: `${t('auditKindFilter')}: ${kindLabel(filters.subjectKind)}`,
            onRemove: () => void load({ subjectKind: '' }),
          },
        ]
      : []),
    ...(filters.subjectId
      ? [
          {
            key: 'subject',
            label: `${t('auditSubject')}: ${filters.subjectId}`,
            onRemove: () => void load({ subjectId: '' }),
          },
        ]
      : []),
    ...(filters.from
      ? [{ key: 'from', label: `${t('from')}: ${dayLabel(filters.from, locale)}`, onRemove: () => void load({ from: '' }) }]
      : []),
    ...(filters.to
      ? [{ key: 'to', label: `${t('to')}: ${dayLabel(filters.to, locale)}`, onRemove: () => void load({ to: '' }) }]
      : []),
  ];

  return (
    <SettingsPage title="auditLog" subtitle="settingsAuditHint" fill>
      <ListCard>
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchAuditHint')}
            total={total ?? undefined}
            shown={items.length}
            chips={chips}
            onClearAll={clearAll}
          >
            <div className="flex flex-col gap-4">
              {/* The actions come from the API, so one recorded tomorrow is
                  filterable tomorrow — not after a release of this screen. */}
              <FilterField label={t('auditAction')}>
                <ToggleGroup
                  options={actions.map((action) => ({ value: action, label: actionLabel(action) }))}
                  selected={filters.actions}
                  onChange={(next) => void load({ actions: next })}
                />
              </FilterField>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <FilterField label={t('actor')} htmlFor="a-actor">
                  <AsyncSelect
                    id="a-actor"
                    value={filters.actorUserId}
                    emptyLabel={t('auditAnyone')}
                    fetchPage={staffOptions.page}
                    fetchOne={async (id): Promise<AsyncOption | null> => {
                      // A customer, or someone whose account is gone, is not on
                      // the staff list — but their rows already say who they were.
                      const known = actorLabel || items.find((entry) => entry.actorUserId === id)?.actorName;
                      if (known) return { id, label: known };
                      return staffOptions.one(id).catch(() => null);
                    }}
                    onChange={(id, option) => filterByActor(id, option?.label ?? '')}
                  />
                </FilterField>

                <FilterField label={t('auditKindFilter')} htmlFor="a-kind">
                  <Select
                    id="a-kind"
                    value={filters.subjectKind}
                    onChange={(event) => void load({ subjectKind: event.target.value })}
                  >
                    <option value="">{t('auditAnyKind')}</option>
                    {kinds.map((entry) => (
                      <option key={entry.kind} value={entry.kind}>
                        {entry.label}
                      </option>
                    ))}
                  </Select>
                </FilterField>

                <FilterField label={t('subjectId')} htmlFor="a-subject">
                  <Input
                    id="a-subject"
                    dir="ltr"
                    placeholder={t('subjectIdHint')}
                    value={subjectDraft}
                    onChange={(event) => setSubjectDraft(event.target.value)}
                  />
                </FilterField>

                <div className="grid grid-cols-2 gap-2">
                  <FilterField label={t('from')} htmlFor="a-from">
                    <Input
                      id="a-from"
                      type="date"
                      dir="ltr"
                      value={filters.from}
                      max={filters.to || undefined}
                      onChange={(event) => void load({ from: event.target.value })}
                    />
                  </FilterField>
                  <FilterField label={t('to')} htmlFor="a-to">
                    <Input
                      id="a-to"
                      type="date"
                      dir="ltr"
                      value={filters.to}
                      min={filters.from || undefined}
                      onChange={(event) => void load({ to: event.target.value })}
                    />
                  </FilterField>
                </div>
              </div>
            </div>
          </FilterBar>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton rows={8} columns={5} />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('noAuditEntries')}
            body={t('noAuditEntriesHint')}
            icon={<ClockIcon />}
            action={
              chips.length > 0 || filters.search ? (
                <Button variant="secondary" onClick={clearAll}>
                  {t('clearFilters')}
                </Button>
              ) : null
            }
          />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('when')}</Th>
                  <Th>{t('actor')}</Th>
                  <Th>{t('auditAction')}</Th>
                  <Th>{t('auditSubject')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => {
                  const changes = diff(entry.before, entry.after);
                  const isOpen = expanded === entry.id;
                  const href = subjectHref(entry.subjectKind, entry.subjectId, entry.action);

                  return (
                    <Fragment key={entry.id}>
                      <Tr
                        onClick={() => setExpanded(isOpen ? null : entry.id)}
                        className={isOpen ? 'bg-surface-sunken/70' : undefined}
                      >
                        <Td className="whitespace-nowrap text-ink-muted">{dateTime(entry.createdAt, locale)}</Td>
                        <Td>
                          <span className="flex flex-wrap items-center gap-2">
                            {entry.actorUserId ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  filterByActor(entry.actorUserId ?? '', entry.actorName ?? '');
                                }}
                                title={t('auditFilterByActor')}
                                className="text-start text-ink hover:underline"
                              >
                                {entry.actorName ?? t('auditAccountGone')}
                              </button>
                            ) : (
                              // A background job or an unauthenticated request is
                              // still an actor; naming it beats an empty cell.
                              <span className="text-ink-muted">{t('systemActor')}</span>
                            )}
                            {entry.actorRole ? <Pill tone="neutral">{roleLabel(entry.actorRole)}</Pill> : null}
                          </span>
                          {entry.actorEmail ? (
                            <span dir="ltr" className="block text-xs text-ink-muted">
                              {entry.actorEmail}
                            </span>
                          ) : null}
                        </Td>
                        <Td>
                          <span className="text-ink" title={entry.action}>
                            {actionLabel(entry.action)}
                          </span>
                        </Td>
                        <Td>
                          {entry.subjectKind ? (
                            href ? (
                              <Link
                                to={href}
                                onClick={(event) => event.stopPropagation()}
                                title={t('auditOpenRecord')}
                                className="block font-medium text-brand-700 hover:underline"
                              >
                                {kindLabel(entry.subjectKind)}
                              </Link>
                            ) : (
                              <span className="block text-ink">{kindLabel(entry.subjectKind)}</span>
                            )
                          ) : null}
                          {entry.subjectId ? (
                            // An id is an identifier: Latin, left to right (ADR-023).
                            <button
                              type="button"
                              dir="ltr"
                              onClick={(event) => {
                                event.stopPropagation();
                                void load({ subjectId: entry.subjectId ?? '' });
                              }}
                              title={t('filterBySubject')}
                              className="block max-w-56 truncate font-mono text-xs text-ink-muted hover:text-ink hover:underline"
                            >
                              {entry.subjectId}
                            </button>
                          ) : null}
                        </Td>
                        <Td align="end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpanded(isOpen ? null : entry.id);
                            }}
                            aria-expanded={isOpen}
                          >
                            {isOpen ? t('hideDetails') : t('details')}
                          </Button>
                        </Td>
                      </Tr>

                      {isOpen ? (
                        <tr className="bg-surface-sunken">
                          <td colSpan={5} className="border-b border-line px-5 py-4">
                            <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 xl:grid-cols-4">
                              <Meta label={t('auditActionCode')} value={entry.action} />
                              <Meta label={t('route')} value={entry.route} />
                              <Meta label={t('ipAddress')} value={entry.ip} />
                              <Meta label={t('requestId')} value={entry.requestId} />
                            </dl>

                            {changes.length === 0 ? (
                              <p className="mt-3 text-xs text-ink-muted">{t('noFieldChanges')}</p>
                            ) : (
                              <table className="mt-3 w-full text-xs">
                                <thead>
                                  <tr className="text-ink-faint">
                                    <th scope="col" className="py-1 text-start font-medium">
                                      {t('field')}
                                    </th>
                                    <th scope="col" className="py-1 text-start font-medium">
                                      {t('beforeValue')}
                                    </th>
                                    <th scope="col" className="py-1 text-start font-medium">
                                      {t('afterValue')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {changes.map((change) => (
                                    <tr key={change.field} className="border-t border-line/60">
                                      <td className="py-1.5 pe-4 font-medium text-ink">{change.field}</td>
                                      <td dir="ltr" className="py-1.5 pe-4 font-mono break-all text-danger">
                                        {change.before}
                                      </td>
                                      <td dir="ltr" className="py-1.5 font-mono break-all text-success">
                                        {change.after}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>
    </SettingsPage>
  );
}

function Meta({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;

  return (
    <div className="flex min-w-0 gap-2">
      <dt className="shrink-0 text-ink-faint">{label}</dt>
      <dd dir="ltr" className="truncate font-mono text-ink-muted">
        {value}
      </dd>
    </div>
  );
}

interface FieldChange {
  field: string;
  before: string;
  after: string;
}

/**
 * The fields that actually differ.
 *
 * A creation has no `before` and a deletion no `after`, so both are treated as
 * empty objects and every field of the other side counts as a change — which is
 * exactly what happened.
 */
function diff(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): FieldChange[] {
  const left = before ?? {};
  const right = after ?? {};
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])].sort();

  return keys
    .map((field) => ({ field, before: render(left[field]), after: render(right[field]) }))
    .filter((change) => change.before !== change.after);
}

function render(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
